import { useTranslations } from 'next-intl'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ProductExcelImportsFilterParams, CreateProductExcelImportParams } from '@/types/product-excel-import'
import { useProductExcelImportsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useProductExcelImports = (params: ProductExcelImportsFilterParams) => {
  const fetchProductExcelImports = useProductExcelImportsStore(state => state.fetchProductExcelImports)

  return useQuery({
    queryKey: ['productExcelImports', params],
    queryFn: () => fetchProductExcelImports(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useProductExcelImport = (id: string) => {
  const getProductExcelImport = useProductExcelImportsStore(state => state.getProductExcelImport)

  return useQuery({
    queryKey: ['productExcelImport', id],
    queryFn: () => getProductExcelImport(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateProductExcelImport = () => {
  const createProductExcelImport = useProductExcelImportsStore(state => state.createProductExcelImport)

  return useMutation({
    mutationFn: (params: CreateProductExcelImportParams) => createProductExcelImport(params),
    onSuccess: () => {}
  })
}

export const useDeleteProductExcelImport = () => {
  const deleteProductExcelImport = useProductExcelImportsStore(state => state.deleteProductExcelImport)

  return useMutation({
    mutationFn: (id: string) => deleteProductExcelImport(id),
    onSuccess: () => {}
  })
}

export const useRetryProductExcelImport = () => {
  const retryProductExcelImport = useProductExcelImportsStore(state => state.retryProductExcelImport)

  return useMutation({
    mutationFn: (id: string) => retryProductExcelImport(id),
    onSuccess: () => {}
  })
}

export const useDownloadProductExcelImportTemplate = () => {
  const t = useTranslations()
  const downloadProductExcelImportTemplate = useProductExcelImportsStore(
    state => state.downloadProductExcelImportTemplate
  )

  return useMutation({
    mutationFn: () => downloadProductExcelImportTemplate(),
    onSuccess: ({ blob, filename }) => {
      // Create a download link for the blob
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      // Use filename from the response or fallback to default
      link.download = filename || `product-import-template.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
    onError: (error: any) => {
      console.error('Error downloading template:', error)
      toast.error(error.response?.data?.message ?? t('error_downloading_file'))
    }
  })
}

export const useBulkDeleteProductExcelImports = () => {
  const bulkDeleteProductExcelImports = useProductExcelImportsStore(state => state.bulkDeleteProductExcelImports)
  const deselectAllProductExcelImports = useProductExcelImportsStore(state => state.deselectAllProductExcelImports)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteProductExcelImports(ids),
    onSuccess: response => {
      deselectAllProductExcelImports()
      handleBulkDeleteSuccess(response)
    }
  })
}
